import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine,
} from 'recharts';
import ThemedChartTooltip from '../../shared/ThemedChartTooltip';
import {
  useAimSnapshotTrend, useAimMentionRateTrend, useAimHeatmap, useAimKpis, useAimHallucinations, useAimRun, useAimShareOfVoice,
  useAimScorecard,
} from '../lib/aimQueries';
import { NO_DATA_MESSAGE, isCollectionFailed } from '../lib/dataStatus';
import { isPending } from '../lib/queryState';
import { colorFor, prettifyProviderLabel } from '../lib/providerColors';
import { pivotTrendByProvider, defaultVisibleProviders, selectedRunMarkerDate, DEFAULT_VISIBLE_PROVIDERS } from '../lib/overviewTrend';
import { prettifyCategory } from '../lib/categoryLabel';
import MetricCard from '../primitives/MetricCard';
import AimEmpty, { AimLoading, AimError } from '../primitives/AimEmpty';
import HeatmapCellPanel from '../panels/HeatmapCellPanel';
import { useTheme } from '../../../contexts/ThemeContext';

// Custom tooltip body for the Mention Rate Trend chart. Rows are sorted by
// value so the strongest providers float to the top.
function TrendTooltipContent({ active, payload }) {
  const { isDarkMode } = useTheme();
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;
  const rows = [...payload].sort((a, b) => Number(b.value ?? 0) - Number(a.value ?? 0));
  return (
    <div
      className={`rounded-lg border text-xs shadow-lg px-3 py-2 ${
        isDarkMode
          ? 'bg-gray-900/95 border-gray-700 text-gray-100'
          : 'bg-white/95 border-gray-200 text-gray-900'
      }`}
    >
      <div className="space-y-0.5">
        {rows.map((row) => (
          <div key={row.dataKey || row.name} className="flex items-center gap-3">
            <span className="flex-1 truncate">{prettifyProviderLabel(row.name || row.dataKey)}</span>
            <span className="tabular-nums font-medium ml-2">
              {row.value != null ? `${Number(row.value).toFixed(1)}%` : '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Below 1650px the legend pill row wraps to two rows which gets cramped (markup
// #71). Render this dropdown there instead — same toggle semantics, less width.
function ProviderMultiSelect({ providers, isOn, onToggle, onSetAll }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const selectedCount = providers.filter((p) => isOn(p)).length;
  const total = providers.length;

  return (
    <div className="relative inline-block" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
      >
        <span>Providers: {selectedCount}/{total}</span>
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M3 4.5 L6 7.5 L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-64 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-800 text-[11px] text-gray-500 dark:text-gray-400">
            <span>Toggle providers</span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => onSetAll(true)} className="text-[#32B1FF] hover:underline focus:outline-none">All</button>
              <span className="text-gray-300 dark:text-gray-700">·</span>
              <button type="button" onClick={() => onSetAll(false)} className="text-[#32B1FF] hover:underline focus:outline-none">None</button>
            </div>
          </div>
          <ul role="listbox" aria-multiselectable="true" className="max-h-72 overflow-y-auto py-1">
            {providers.map((p) => {
              const c = colorFor(p);
              const active = isOn(p);
              return (
                <li key={p}>
                  <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => onToggle(p)}
                      className="h-3.5 w-3.5 accent-[#32B1FF]"
                    />
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: c.hex, opacity: c.faded ? 0.5 : 1, border: c.faded ? `1px dashed ${c.hex}` : 'none' }}
                      aria-hidden="true"
                    />
                    <span className="truncate">{prettifyProviderLabel(p)}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function makeRunTypeDot(_runTypeByDate, color) {
  return function RunTypeDot({ cx, cy, payload, index }) {
    if (cx == null || cy == null) return null;
    const k = `dot-${payload?.date ?? index}`;
    // Uniform circle for every data point regardless of run type. The
    // Weekly-vs-Full distinction still surfaces via the run picker label and
    // the tooltip's date text.
    return <circle key={k} cx={cx} cy={cy} r={3} fill={color} stroke={color} />;
  };
}

const HEATMAP_STOPS = [
  { t: 0, r: 255, g: 255, b: 255 }, // white
  { t: 0.5, r: 167, g: 243, b: 208 }, // emerald-200 (light green)
  { t: 1, r: 6, g: 95, b: 70 }, // emerald-800 (dark green)
];

function heatmapColor(t) {
  let i = 0;
  while (i < HEATMAP_STOPS.length - 1 && t > HEATMAP_STOPS[i + 1].t) i += 1;
  const a = HEATMAP_STOPS[i];
  const b = HEATMAP_STOPS[i + 1] || a;
  const span = b.t - a.t || 1;
  const k = (t - a.t) / span;
  return {
    r: Math.round(a.r + (b.r - a.r) * k),
    g: Math.round(a.g + (b.g - a.g) * k),
    b: Math.round(a.b + (b.b - a.b) * k),
  };
}

/**
 * One heatmap cell. A null value is a dash and never clickable, whatever `onClick` says: it is the
 * control that keeps an unmeasured cell from opening an empty drill-down.
 *
 * @param {{value: number|null, onClick?: Function, ariaLabel?: string, noDataTitle?: string}} props
 *   `noDataTitle` replaces the default hover text on a dash, for a provider whose collection failed.
 */
function HeatmapCell({ value, onClick, ariaLabel, noDataTitle = 'No data for this run' }) {
  if (value == null) {
    return (
      <div
        className="h-7 w-full flex items-center justify-center text-[10px] text-gray-400 dark:text-gray-600 bg-gray-50 dark:bg-gray-800/40"
        title={noDataTitle}
      >
        —
      </div>
    );
  }
  const v = Math.max(0, Math.min(100, Number(value)));
  const { r, g, b } = heatmapColor(v / 100);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const textColor = luminance > 0.6 ? '#111827' : '#ffffff';
  const clickable = typeof onClick === 'function';
  const commonProps = {
    className: `h-7 w-full flex items-center justify-center text-[10px] ${clickable ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#32B1FF] focus:ring-inset' : ''}`,
    style: { backgroundColor: `rgb(${r}, ${g}, ${b})`, color: textColor },
    title: `${v.toFixed(1)}%`,
  };
  if (!clickable) return <div {...commonProps}>{v.toFixed(0)}</div>;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      {...commonProps}
    >
      {v.toFixed(0)}
    </div>
  );
}

export default function OverviewTab({ clientId, runId, compareRunId }) {
  const { data: trend, loading: lTrend } = useAimMentionRateTrend(clientId, 30);
  const { data: trendP1, loading: lTrendP1 } = useAimSnapshotTrend(clientId, 'P1');
  const { data: heatmap, loading: lHeatmapQuery } = useAimHeatmap(clientId, runId);
  // The heatmap reports a provider whose collection failed as 0% over its responses, with no status
  // of its own, so the scorecard (which does carry one) says which providers to draw as no data.
  // The grid waits for it rather than flash those cells as measured zeros.
  const scorecardQuery = useAimScorecard(clientId, runId);
  const failedProviders = useMemo(
    () => new Set((Array.isArray(scorecardQuery.data) ? scorecardQuery.data : [])
      .filter(isCollectionFailed)
      .map((r) => r.display_name)),
    [scorecardQuery.data],
  );
  const lHeat = lHeatmapQuery || isPending(scorecardQuery);
  // Honor the "Compare to" dropdown literally — when the user picks "— None —"
  // (compareRunId is null), don't auto-fall-back to the previous run, so KPI
  // trend arrows hide instead of pretending there's a comparison.
  const { data: kpiResp, loading: lKpis } = useAimKpis(clientId, runId, compareRunId);
  // Pull the same hallucinations RPC the Diagnostics grid uses so the
  // "Hallucinations" KPI on Overview reads the same number that the grid
  // shows. The KPI RPC and the Hallucinations RPC have drifted in the past
  // (e.g., 35 vs 37); preferring the grid's row count keeps the dashboard
  // self-consistent without exposing the backend gap to customers.
  const { data: hallucRows } = useAimHallucinations(clientId, runId);
  // Pull the run row so we can derive Responses = Calls − Errors. The KPI
  // RPC's `total_responses` has drifted from the run's calls/errors in the
  // past (e.g., 308 calls / 307 responses / 0 errors); deriving from
  // calls/errors guarantees the math closes and matches the Run Status card.
  const { data: run } = useAimRun(clientId, runId);
  const { data: compareRun } = useAimRun(clientId, compareRunId);
  // Pull Share of Voice so the Client Mentions KPI matches the per-park
  // count shown on CompetitorsTab. The KPI RPC counts unique responses while
  // SoV counts raw mention rows (incl. multiple mentions per response and
  // mentions on errored responses) — picking SoV as canonical so both
  // surfaces show the same number.
  const { data: sov } = useAimShareOfVoice(clientId, runId);
  const { data: compareSov } = useAimShareOfVoice(clientId, compareRunId);

  const [enabledProviders, setEnabledProviders] = useState({});
  const [drilldown, setDrilldown] = useState(null);
  const [openCategories, setOpenCategories] = useState({});

  const trendData = useMemo(() => {
    const source = (Array.isArray(trend) && trend.length > 0) ? trend : trendP1;
    return pivotTrendByProvider(source);
  }, [trend, trendP1]);

  // Top 5 by average mention rate are on by default; the reader's own toggles win.
  // With every provider on (11 for a typical run) the lines were an unreadable tangle.
  const defaultOn = useMemo(
    () => defaultVisibleProviders(trendData.rows, trendData.providers, DEFAULT_VISIBLE_PROVIDERS),
    [trendData],
  );
  const isOn = (p) => enabledProviders[p] ?? defaultOn.has(p);
  const visibleProviders = trendData.providers.filter(isOn);
  // A "Selected run" line ties the chart to the run picked in the header.
  const markerDate = selectedRunMarkerDate(trendData.rows, run?.run_date);

  const kpis = kpiResp?.current || null;
  const prevKpis = kpiResp?.previous || null;

  // Prefer the row count from the hallucinations RPC (same source as the
  // Diagnostics grid) so the KPI and the grid never disagree. Fall back to
  // the KPI RPC's pre-aggregated number while the grid request is still
  // in-flight.
  const hallucCount = Array.isArray(hallucRows) ? hallucRows.length : null;
  const hallucKpiValue = hallucCount != null ? hallucCount : kpis?.total_hallucinations;

  // Derive Responses from Calls − Errors when the run row is loaded; fall
  // back to the KPI RPC while it's still in-flight so the tile isn't blank.
  const derivedResponses = run && run.total_api_calls != null
    ? Number(run.total_api_calls) - Number(run.total_errors ?? 0)
    : null;
  const responsesValue = derivedResponses != null ? derivedResponses : kpis?.total_responses;
  const derivedPrevResponses = compareRun && compareRun.total_api_calls != null
    ? Number(compareRun.total_api_calls) - Number(compareRun.total_errors ?? 0)
    : null;

  // Derive Client Mentions from the SoV client-park row so the KPI matches
  // the count shown on CompetitorsTab. Falls back to kpis.client_mentions
  // while SoV is loading so the tile isn't blank.
  const sovClientMentions = Array.isArray(sov)
    ? sov.find((r) => r.is_client_park)?.mentions
    : null;
  const clientMentionsValue = sovClientMentions != null ? sovClientMentions : kpis?.client_mentions;
  const sovPrevClientMentions = Array.isArray(compareSov)
    ? compareSov.find((r) => r.is_client_park)?.mentions
    : null;

  const kpiCards = kpis ? [
    { label: 'Responses', value: responsesValue, suffix: '', prevValueOverride: derivedPrevResponses, prevKey: 'total_responses' },
    { label: 'Property Mentions', value: clientMentionsValue, suffix: '', tint: 'bg-emerald-50 dark:bg-emerald-950/30', prevValueOverride: sovPrevClientMentions, prevKey: 'client_mentions' },
    { label: 'Competitors Detected', value: kpis.unique_competitors, suffix: '', tint: 'bg-sky-50 dark:bg-sky-950/30', prevKey: 'unique_competitors' },
    { label: 'Citation Sources', value: kpis.citation_sources, suffix: '', tint: 'bg-purple-50 dark:bg-purple-950/30', prevKey: 'citation_sources' },
    {
      label: 'Hallucinations',
      value: hallucKpiValue,
      suffix: '',
      tint: (hallucKpiValue ?? 0) > 0 ? 'bg-red-50 dark:bg-red-950/30' : undefined,
      prevKey: 'total_hallucinations',
    },
    { label: 'Avg Latency', value: kpis.avg_latency_ms, suffix: 'latency', prevKey: 'avg_latency_ms' },
  ].map(({ prevKey, prevValueOverride, ...c }) => {
    if (prevValueOverride != null) return { ...c, prevValue: prevValueOverride };
    if (!prevKpis || prevKpis[prevKey] == null) return c;
    return { ...c, prevValue: prevKpis[prevKey] };
  }) : [];

  return (
    <div className="space-y-5">
      {/* 2A Trend */}
      <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 max-[499px]:px-2">
        <div className="flex flex-row items-center justify-between gap-2 mb-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Mention Rate Trend</h3>
          {/* Pills at >= 1650px, dropdown below to avoid the cramped two-row wrap (markup #71) */}
          <div className="hidden min-[1650px]:flex flex-wrap gap-1.5">
            {trendData.providers.map((p) => {
              const c = colorFor(p);
              const active = isOn(p);
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setEnabledProviders((prev) => ({ ...prev, [p]: !active }))}
                  className={`whitespace-nowrap px-2 py-1 rounded-full text-[11px] font-medium ${active ? 'text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}
                  style={active ? { backgroundColor: c.hex, opacity: c.faded ? 0.6 : 1 } : undefined}
                >
                  {prettifyProviderLabel(p)}
                </button>
              );
            })}
          </div>
          <div className="min-[1650px]:hidden">
            <ProviderMultiSelect
              providers={trendData.providers}
              isOn={isOn}
              onToggle={(p) => setEnabledProviders((prev) => ({ ...prev, [p]: !isOn(p) }))}
              onSetAll={(on) => setEnabledProviders(
                () => Object.fromEntries(trendData.providers.map((p) => [p, on])),
              )}
            />
          </div>
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3">
          How often each AI provider mentioned your property across recent runs. Use it to spot providers gaining or losing visibility over time.
        </p>
        {(lTrend && lTrendP1) ? (
          <AimLoading height={260} />
        ) : trendData.rows.length === 0 ? (
          <AimEmpty message="No trend data yet — wait for a few completed runs." height={220} />
        ) : (
          <div className="rotate-x-labels">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trendData.rows} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
                <CartesianGrid stroke="rgba(148,163,184,0.2)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                <ThemedChartTooltip content={<TrendTooltipContent />} />
                {markerDate && (
                  <ReferenceLine
                    x={markerDate}
                    stroke="#32B1FF"
                    strokeDasharray="4 3"
                    label={{ value: 'Selected run', position: 'insideTopRight', fontSize: 11, fill: '#32B1FF' }}
                  />
                )}
                {visibleProviders.map((p) => {
                  const c = colorFor(p);
                  return (
                    <Line
                      key={p}
                      type="monotone"
                      dataKey={p}
                      stroke={c.hex}
                      strokeOpacity={c.faded ? 0.5 : 1}
                      dot={makeRunTypeDot(trendData.runTypeByDate, c.hex)}
                      activeDot={{ r: 5 }}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
            {/* Legend below the plot (never over the lines) for the providers drawn. */}
            <div data-testid="trend-legend" className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-600 dark:text-gray-300">
              {visibleProviders.map((p) => (
                <span key={p} className="inline-flex items-center gap-1.5">
                  <span className="h-0.5 w-4 rounded-full" style={{ backgroundColor: colorFor(p).hex }} aria-hidden="true" />
                  {prettifyProviderLabel(p)}
                </span>
              ))}
              {visibleProviders.length < trendData.providers.length && (
                <span className="text-gray-400 dark:text-gray-500">
                  {`+${trendData.providers.length - visibleProviders.length} more — use the provider toggle to show them`}
                </span>
              )}
              {markerDate && (
                <span className="inline-flex items-center gap-1.5 text-[#0B6FB0] dark:text-[#32B1FF]">
                  <span className="h-3 border-l-2 border-dashed border-[#32B1FF]" aria-hidden="true" />
                  Selected run
                </span>
              )}
            </div>
          </div>
        )}
      </section>

      {/* 2B Heatmap */}
      <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Category × Provider Heatmap</h3>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
          Mention rate for each query category, broken down by AI provider, for the selected run. Brighter cells mean the provider mentions you more often in that category. Click a cell to see the underlying responses.
        </p>
        {lHeat ? (
          <AimLoading height={160} />
        ) : scorecardQuery.error ? (
          // Without the scorecard a failed provider's 0% cannot be told from a real one, so draw no
          // cells rather than an unverifiable measurement.
          <AimError message="Couldn't check which providers have data for this run." onRetry={scorecardQuery.refresh} />
        ) : !heatmap || !Array.isArray(heatmap.cells) || heatmap.cells.length === 0 ? (
          <AimEmpty message="No heatmap data for this run." height={160} />
        ) : (
          <div className="responsive-card-table-wrapper overflow-x-auto">
            <table className="responsive-card-table text-xs w-full">
              <thead>
                <tr className="align-bottom">
                  <th className="sticky left-0 z-10 bg-white dark:bg-gray-900 text-left text-gray-500 dark:text-gray-400 font-normal pr-2">&nbsp;</th>
                  {heatmap.providers.map((p) => (
                    <th key={p} className="text-left text-gray-500 dark:text-gray-400 px-1 py-1 font-normal min-w-[100px] align-bottom">
                      <div className="whitespace-nowrap pb-1">{prettifyProviderLabel(p)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {heatmap.categories.map((cat) => {
                  const cellByProvider = Object.fromEntries(
                    heatmap.cells
                      .filter((c) => c.category === cat)
                      .map((c) => [c.provider_name, c])
                  );
                  const isOpen = !!openCategories[cat];
                  return (
                    <tr
                      key={cat}
                      className={`${isOpen ? 'max-[499px]:!bg-[#32B1FF]/10' : 'max-[499px]:!bg-transparent rc-collapsed'}`}
                    >
                      <td
                        data-label={prettifyCategory(cat)}
                        onClick={() => setOpenCategories((o) => ({ ...o, [cat]: !o[cat] }))}
                        className="rc-title-cell sticky left-0 z-10 bg-white dark:bg-gray-900 py-0.5 pr-2 text-gray-700 dark:text-gray-300 whitespace-nowrap font-semibold max-[499px]:!bg-transparent max-[499px]:cursor-pointer"
                      >
                        <span>{prettifyCategory(cat)}</span>
                        <svg className={`hidden max-[499px]:block w-3 h-3 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </td>
                      {heatmap.providers.map((p) => {
                        const cell = cellByProvider[p];
                        const failed = failedProviders.has(p);
                        const v = failed ? null : cell?.mention_pct;
                        return (
                          <td key={p} data-label={prettifyProviderLabel(p)} className="py-0.5 px-0.5 min-w-[100px]">
                            <div className="max-[499px]:w-[100px] max-[344px]:!w-[50px]">
                              <HeatmapCell
                                value={v}
                                noDataTitle={failed ? NO_DATA_MESSAGE : undefined}
                                onClick={v != null ? () => setDrilldown({ category: cat, providerName: p }) : undefined}
                                ariaLabel={v != null ? `Drill into ${cat} × ${prettifyProviderLabel(p)}` : undefined}
                              />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!lHeat && !scorecardQuery.error && heatmap && Array.isArray(heatmap.cells) && heatmap.cells.length > 0 && (
          <p className="mt-3 text-[11px] text-gray-500 dark:text-gray-400">
            Cells marked &ldquo;&mdash;&rdquo; mean the category had no responses from that provider in this run &mdash; either the prompt wasn&rsquo;t executed (e.g. competitive and multi-turn prompts only run in monthly full runs) or the provider returned an error.
          </p>
        )}
      </section>

      {/* 2C KPI cards */}
      <section className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {lKpis ? (
          <div className="col-span-6"><AimLoading height={90} /></div>
        ) : kpiCards.length === 0 ? (
          <div className="col-span-6"><AimEmpty message="No KPI data for this run." height={90} /></div>
        ) : (
          kpiCards.map((c) => <MetricCard key={c.label} {...c} />)
        )}
      </section>

      {drilldown && (
        <HeatmapCellPanel
          clientId={clientId}
          runId={runId}
          category={drilldown.category}
          providerName={drilldown.providerName}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
  );
}
